import { useTranslation } from "react-i18next"
import {
  PaginationState,
  SortingState,
  createTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useTableInstance,
} from "@tanstack/react-table"
import { useCallback, useEffect, useMemo, useState } from "react"
import ReactSelect from "react-select"
import { Button } from "@inkblot-therapy/squid"
import { useNavigate } from "react-router-dom"
import { Referral } from "../../types/Referral"
import { StatusBadge } from "../StatusBadge"
import { formatReferralDate } from "../../utils/formatReferralDate"
import { PaginationMeta } from "../../types/PaginationMeta"
import { useDebounce } from "../../hooks/useDebounce"
import { ReferralTableHeader } from "./ReferralTableHeader"
import { ReferralTableRow } from "./ReferralTableRow"
import { ReferralTableFooter } from "./ReferralTableFooter"

const table = createTable().setRowType<Partial<Referral>>()

const initialSortKey: Record<string, string> = {
  referral_status_order: "status",
  referral_complete: "referral_complete",
  referral_date: "referral_date",
  name: "name",
}

function parseInitialSort(initialSort: string) {
  const desc = initialSort?.includes("desc")
  const key = initialSort.split("_").slice(0, -1).join("")
  const id = initialSortKey[key]
  return { id, desc }
}

interface Props {
  data: Array<Referral>
  paginationMeta?: PaginationMeta
  setPage?: (page: number) => unknown
  setSort?: (sortState: SortingState) => unknown
  initialPage?: number
  initialSort?: string
  setFilter?: (filter: { key: string; value: string } | undefined) => unknown
  initialFilter?: { key: string; value: string }
}

export function ReferralTable({
  data,
  paginationMeta,
  setPage,
  initialPage,
  setSort,
  initialSort,
  setFilter,
  initialFilter,
}: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const referralFilterOptions = useMemo<
    Array<{
      value: "name" | "client_id" | "service_id"
      label: JSX.Element
    }>
  >(
    () => [
      {
        value: "name",
        label: <p>{t("admin.inputs.byName")}</p>,
      },
      {
        value: "service_id",
        label: <p>{t("admin.inputs.byServiceRequestID")}</p>,
      },
      {
        value: "client_id",
        label: <p>{t("admin.inputs.byReferralID")}</p>,
      },
    ],
    [t],
  )

  const defaultColumns = useMemo(
    () => [
      table.createDataColumn(
        (info) => ({
          name: info.first_name + " " + info.last_name,
          email: info.client_email,
        }),
        {
          id: "name",
          cell: (info) => (
            <div>
              <p>
                <strong>{info.getValue().name}</strong>
              </p>
              <p>{info.getValue().email}</p>
            </div>
          ),
          header: () => <span>{t("admin.table.headers.name")}</span>,
          footer: () => null,
        },
      ),
      table.createDataColumn(
        (info) =>
          info.service_id ? info.service_id : info.service_request_id_outgoing,
        {
          id: "service_id",
          cell: (info) => {
            const value = info.getValue()
            return value ? value : "-"
          },
          header: () => (
            <span>{t("admin.table.headers.serviceRequestId")}</span>
          ),
          footer: () => null,
          enableSorting: false,
        },
      ),
      table.createDataColumn("client_id", {
        header: () => <span>{t("admin.table.headers.referralId")}</span>,
        footer: () => null,
        enableSorting: false,
      }),
      table.createDataColumn("client_province", {
        header: () => <span>{t("admin.table.headers.organization")}</span>,
        footer: () => null,
        enableSorting: false,
      }),
      table.createDataColumn(
        (info) => (info.referral_date ? new Date(info.referral_date) : null),
        {
          id: "referral_date",
          cell: (info) => {
            const value = info.getValue()
            return value ? formatReferralDate(value) : "-"
          },
          header: () => <span>{t("admin.table.headers.dateReceived")}</span>,
          footer: () => null,
        },
      ),
      table.createDataColumn(
        (info) =>
          info.referral_complete ? new Date(info.referral_complete) : null,
        {
          id: "referral_complete",
          cell: (info) => {
            const value = info.getValue()
            return value ? formatReferralDate(value) : "-"
          },
          header: () => <span>{t("admin.table.headers.dateComplete")}</span>,
          footer: () => null,
        },
      ),
      table.createDataColumn("status", {
        cell: (info) => {
          const value = info.getValue()
          return <StatusBadge status={value} />
        },
        header: () => <span>{t("admin.table.headers.status")}</span>,
        footer: () => null,
      }),
    ],
    [t],
  )

  const [columns] = useState<typeof defaultColumns>(() => [...defaultColumns])
  const [globalFilter, setGlobalFilter] = useState(
    initialFilter ? initialFilter.value : "",
  )
  const globalFilterDebounced = useDebounce(globalFilter, 300)
  const [sorting, setSorting] = useState<SortingState>(
    initialSort ? [parseInitialSort(initialSort)] : [],
  )
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex:
      initialPage !== undefined
        ? initialPage - 1
        : (paginationMeta?.current_page ?? 1) - 1,
    pageSize: paginationMeta?.per_page ?? 10,
  }))
  const [columnVisibility] = useState({
    first_name: false,
    last_name: false,
    client_email: false,
    full_name: false,
  })

  const [filterValue, setFilterValue] = useState(
    initialFilter ? initialFilter.key : "name",
  )

  const referralFilterFieldValue = referralFilterOptions.find(
    (option) => option.value === filterValue,
  )

  const handleReferralFilterOptionChange = useCallback(
    (option: typeof referralFilterOptions[number] | null) => {
      if (option?.value) {
        setFilterValue(option?.value)
      }
    },
    [],
  )

  const instance = useTableInstance(table, {
    data,
    columns,
    state: {
      columnVisibility,
      globalFilter,
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "auto",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    pageCount: paginationMeta?.total_pages,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  })

  useEffect(() => {
    setPage?.(pagination.pageIndex + 1)
  }, [pagination.pageIndex, setPage])

  useEffect(() => {
    setSort?.(sorting)
  }, [setSort, sorting])

  useEffect(() => {
    setFilter?.({ key: filterValue, value: globalFilterDebounced })
  }, [filterValue, globalFilterDebounced, setFilter])

  const renderPlaceholder = (filter: string) => {
    const filterMap = {
      name: "admin.inputs.searchByName",
      service_id: "admin.inputs.searchByServiceRequestID",
      client_id: "admin.inputs.searchByReferralID",
    }
    return filter === "name" ||
      filter === "client_id" ||
      filter === "service_id"
      ? filterMap[filter]
      : ""
  }

  return (
    <div className="w-full text-[color:var(--alias-color-text-weaker)] [font-size:var(--alias-font-label-weak-font-size)]">
      <div className="w-full flex flex-row justify-start">
        <div className="mb-4 flex flex-row w-full">
          <input
            aria-label={t("admin.inputs.search")}
            className="rounded-lg z-5 h-[42px] pl-3 w-full placeholder:text-[color:var(--alias-color-text-weaker)] max-w-[480px] border border-[color:var(--alias-color-border-base)] border-1 border-r-0 rounded-r-none"
            value={globalFilter ?? initialFilter?.value ?? ""}
            onChange={(e) => setGlobalFilter(String(e.target.value))}
            placeholder={t(renderPlaceholder(filterValue))}
          />
          <div className="border border-[color:var(--alias-color-border-base)] border-1 border-l-0 rounded-lg rounded-l-none pr-1">
            <ReactSelect
              // these are necessary in order to override react-select styles via classNames
              className="react-select no-border min-w-[220px]"
              classNamePrefix="react-select"
              options={referralFilterOptions}
              // this is the actual id that gets matched with our label component
              inputId="referralStatus"
              onChange={handleReferralFilterOptionChange}
              value={referralFilterFieldValue}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={false}
              isSearchable={false}
              styles={{
                option: (provided, _state) => ({
                  ...provided,
                  color: "var(--alias-color-text-base)",
                }),
              }}
            />
          </div>
        </div>
        <Button
          variant="primary"
          className="whitespace-nowrap my-squid mb-4"
          onClick={() => {
            sessionStorage.setItem("origin", "admin")
            navigate("/novascotia/steps/1")
          }}
        >
          {t("admin.buttons.createNewReferral")}
        </Button>
      </div>
      <table
        className="w-full border-collapse outline outline-[color:var(--alias-color-border-base)] outline-1 rounded-lg overflow-hidden"
        cellPadding="0"
        cellSpacing="0"
        data-testid="referral-table"
      >
        <thead className="text-[12px] uppercase h-[42px] bg-[color:var(--alias-color-bg-alt)] [font-weight:var(--alias-font-body-lg-bold-font-weight)]">
          {instance.getHeaderGroups().map((headerGroup) => (
            <ReferralTableHeader
              headerGroup={headerGroup}
              key={headerGroup.id}
            />
          ))}
        </thead>
        <tbody>
          {instance.getRowModel().rows.map((row, index) => (
            <ReferralTableRow row={row} index={index} key={row.id} />
          ))}
        </tbody>
      </table>
      <ReferralTableFooter
        instance={instance}
        data={data}
        paginationMeta={paginationMeta}
      />
    </div>
  )
}
